import {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
  KeyboardEvent,
  ChangeEvent,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setOpenAmberCopilot,
  setCopilotInitialMessage,
} from "@Pages/PropertyPage/components/ZendeskChat/ChatBotReducer";
import { AppState } from "@Store/index";
import Button from "@Components/Button";
import wrapperHOC from "@Utils/wrapperHOC";
import SmartFilterLogo from "@Pages/Search/components/FilterSection/components/SmartFilters/icons/SmartFilterLogo";
import useTranslationHook from "hooks/useTranslationHook";
import { getRandomQuestions } from "./smartQuestionsData";
import useShowSmartQuestions from "./useShowSmartQuestions";
import { trackSmartQuestionChipClick, trackSmartQuestionSubmitted } from "./analytics";
import styles from "./smartQuestions.module.scss";

interface SmartQuestionsProps {
  /** Pre-computed questions to display */
  questions?: string[];
  /** Questions to exclude when generating internally */
  excludeQuestions?: string[];
}

/** Arrow icon inside chips (rotated 180°) */
const ChipArrowIcon = () => (
  <svg
    className={styles.chipIcon}
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M2.4 5.1C2.4 4.88 2.22 4.7 2 4.7C1.78 4.7 1.6 4.88 1.6 5.1V7.5C1.6 8.82 2.67 9.9 4 9.9H13.03L10.12 12.82C9.96 12.97 9.96 13.23 10.12 13.38C10.27 13.54 10.53 13.54 10.68 13.38L14.28 9.78C14.36 9.71 14.4 9.6 14.4 9.5C14.4 9.39 14.36 9.29 14.28 9.22L10.68 5.62C10.53 5.46 10.27 5.46 10.12 5.62C9.96 5.77 9.96 6.03 10.12 6.18L13.03 9.1H4C3.12 9.1 2.4 8.38 2.4 7.5V5.1Z"
      fill="#111928"
    />
  </svg>
);

/** Send arrow icon */
const SendIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M15.83 8.42C16.05 8.19 16.05 7.81 15.83 7.58L9.55 1.06C9.33 0.83 8.96 0.83 8.74 1.06C8.52 1.29 8.52 1.67 8.74 1.9L14.05 7.41H0.57C0.26 7.41 0 7.67 0 8C0 8.33 0.26 8.59 0.57 8.59H14.05L8.74 14.1C8.52 14.33 8.52 14.71 8.74 14.94C8.96 15.17 9.33 15.17 9.55 14.94L15.83 8.42Z"
      fill="white"
    />
  </svg>
);

const SmartQuestions = ({
  questions: questionsProp,
  excludeQuestions = [],
}: SmartQuestionsProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslationHook();
  const [inputValue, setInputValue] = useState("");
  const showSmartQuestions = useShowSmartQuestions();
  const isChatbotOpen = useSelector(({ chatBotReducer }: AppState) => chatBotReducer.isChatbotOpen);

  const questionKeys = useMemo(
    () => questionsProp ?? getRandomQuestions(excludeQuestions),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [questionsProp],
  );

  const openCopilot = useCallback(
    (message?: string) => {
      if (message) {
        dispatch(setCopilotInitialMessage(message));
      }
      if (!isChatbotOpen) {
        dispatch(setOpenAmberCopilot(true));
      }
    },
    [dispatch, isChatbotOpen],
  );

  const handleChipClick = useCallback(
    (key: string, translatedText: string) => {
      trackSmartQuestionChipClick({
        section: "smart_questions",
        questionKey: key,
        isChatbotOpen,
      });
      openCopilot(translatedText);
    },
    [openCopilot, isChatbotOpen],
  );

  const handleSend = useCallback(() => {
    const trimmedInput = inputValue.trim();
    if (trimmedInput) {
      trackSmartQuestionSubmitted({
        section: "smart_questions",
        questionText: trimmedInput,
        isChatbotOpen,
      });
      openCopilot(trimmedInput);
      setInputValue("");
    }
  }, [inputValue, openCopilot, isChatbotOpen]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        handleSend();
      }
    },
    [handleSend],
  );

  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  }, []);

  const renderChip = (key: string) => {
    const text = t(key);
    return (
      <Button
        key={key}
        variant="none"
        className={styles.chip}
        onClick={() => handleChipClick(key, text)}
        dataTestId={`smart-question-chip-${key}`}
      >
        <ChipArrowIcon />
        <span>{text}</span>
      </Button>
    );
  };

  const firstRow = questionKeys.slice(0, 3);
  const secondRow = questionKeys.slice(3, 6);

  // Track scroll position for mask classes
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [mask, setMask] = useState<"right" | "left" | "both" | "none">("right");

  const getMaskState = (el: HTMLDivElement): "right" | "left" | "both" | "none" => {
    const canScrollRight = el.scrollWidth > el.clientWidth + el.scrollLeft + 1;
    const canScrollLeft = el.scrollLeft > 1;
    if (canScrollLeft && canScrollRight) return "both";
    if (canScrollLeft) return "left";
    if (canScrollRight) return "right";
    return "none";
  };

  const updateMasks = useCallback(() => {
    if (wrapperRef.current) setMask(getMaskState(wrapperRef.current));
  }, []);

  useEffect(() => {
    updateMasks();
    if (typeof window !== "undefined") {
      window.addEventListener("resize", updateMasks);
      return () => window.removeEventListener("resize", updateMasks);
    }
    return undefined;
  }, [updateMasks, questionKeys]);

  const handleScroll = useCallback(() => {
    updateMasks();
  }, [updateMasks]);

  const getMaskClass = (maskVal: "right" | "left" | "both" | "none") => {
    if (maskVal === "both") return styles.chipsRowMaskBoth;
    if (maskVal === "left") return styles.chipsRowMaskLeft;
    if (maskVal === "right") return styles.chipsRowMaskRight;
    return "";
  };

  if (!showSmartQuestions) return null;

  return (
    <div className={styles.container}>
      {/* Mobile header */}
      <div className={styles.header}>
        <SmartFilterLogo id="sqMobileIcon" />
        <span className={styles.headerText}>{t("propertypage.smartquestions.headertext")}</span>
        <span className={styles.newBadge}>{t("propertypage.smartquestions.newbadge")}</span>
      </div>

      {/* Desktop left panel */}
      <div className={styles.desktopLeft}>
        <SmartFilterLogo id="sqDesktopIcon" size={32} />
        <div className={styles.desktopTitle}>
          {t("propertypage.smartquestions.desktoptitle1")}
          <br />
          {t("propertypage.smartquestions.desktoptitle2")}
        </div>
        <div className={styles.desktopSubtitle}>{t("propertypage.smartquestions.subtitle")}</div>
      </div>

      {/* Right content (input + chips) */}
      <div className={styles.content}>
        <div className={styles.inputOuter}>
          <div className={styles.inputInner}>
            <input
              type="text"
              className={styles.inputField}
              placeholder={t("propertypage.smartquestions.placeholder")}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              aria-label={t("propertypage.smartquestions.headertext")}
            />
            <Button
              variant="none"
              className={`${styles.sendButton} ${inputValue.trim() ? styles.sendButtonActive : ""}`}
              onClick={handleSend}
              dataTestId="smart-questions-send"
            >
              <SendIcon />
            </Button>
          </div>
        </div>

        <div
          className={`${styles.chipsWrapper} ${getMaskClass(mask)}`}
          ref={wrapperRef}
          onScroll={handleScroll}
        >
          <div className={styles.chipsRow}>{firstRow.map(renderChip)}</div>
          <div className={styles.chipsRow}>{secondRow.map(renderChip)}</div>
        </div>
      </div>
    </div>
  );
};

export default wrapperHOC(SmartQuestions, {
  componentName: "SmartQuestions-PropertyPage",
  showForChina: false,
  showForWhiteLable: true,
});
